import { GOOGLE_WALLET_GEOFENCE_RADIUS_M } from './searchableMapConstants';

export function parseLocationCoord(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function circlePath(
  lat: number,
  lng: number,
  radiusMeters: number,
  points = 64
): { lat: number; lng: number }[] {
  const path: { lat: number; lng: number }[] = [];
  const latRad = (lat * Math.PI) / 180;
  const degPerMeterLat = 1 / 111320;
  const degPerMeterLng = 1 / (111320 * Math.cos(latRad));
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    path.push({
      lat: lat + radiusMeters * degPerMeterLat * Math.cos(angle),
      lng: lng + radiusMeters * degPerMeterLng * Math.sin(angle),
    });
  }
  return path;
}

export function pointOnCircleBorder(
  lat: number,
  lng: number,
  radiusMeters: number,
  angleRad = 0
): { lat: number; lng: number } {
  const latRad = (lat * Math.PI) / 180;
  const degPerMeterLat = 1 / 111320;
  const degPerMeterLng = 1 / (111320 * Math.cos(latRad));
  return {
    lat: lat + radiusMeters * degPerMeterLat * Math.cos(angleRad),
    lng: lng + radiusMeters * degPerMeterLng * Math.sin(angleRad),
  };
}

/**
 * Approximate lat/lng from a screen point over the map (bounds-linear).
 * Good enough for short drag-to-resize segments at typical builder zoom.
 */
export function clientXYToLatLngFromMapBounds(
  map: google.maps.Map,
  clientX: number,
  clientY: number
): { lat: number; lng: number } | null {
  const bounds = map.getBounds();
  if (!bounds) return null;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const div = map.getDiv();
  const rect = div.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;
  if (w <= 0 || h <= 0) return null;
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const lng = sw.lng() + (ne.lng() - sw.lng()) * (x / w);
  const lat = ne.lat() - (ne.lat() - sw.lat()) * (y / h);
  return { lat, lng };
}

export function distanceBetweenPointsMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function placedGeofencesOverlap(
  locs: { latitude: number; longitude: number; radius?: number }[]
): boolean {
  if (locs.length < 2) return false;
  const g = GOOGLE_WALLET_GEOFENCE_RADIUS_M;
  const googleTouch = g + g;
  for (let i = 0; i < locs.length; i++) {
    const apple1 = locs[i].radius ?? 80;
    for (let j = i + 1; j < locs.length; j++) {
      const apple2 = locs[j].radius ?? 80;
      const d = distanceBetweenPointsMeters(
        locs[i].latitude,
        locs[i].longitude,
        locs[j].latitude,
        locs[j].longitude
      );
      if (d < apple1 + apple2) return true;
      if (d < googleTouch) return true;
    }
  }
  return false;
}
